/**
 * 音楽再生エンジン（React 非依存のシングルトン）
 *
 * useMusicStore を購読し、単一の HTMLAudioElement を操作する。
 * iOS PWA でバックグラウンド連続再生を成立させるため、以下を守っている。
 *
 * 1. Audio インスタンスは生成したら破棄せず、src だけを差し替えて使い回す
 * 2. Media Session API を常に最新化し、OS から「再生中のアプリ」と認識させ続ける
 * 3. 曲の繋ぎ目で無音時間を作らない。次曲の音声データを Blob で先読みしておき、
 *    ended ハンドラでは await を挟まず同期的に src 差し替え → play() する
 * 4. 最初のユーザー操作で無音データを一度再生し、要素の自動再生ロックを解除する
 *
 * エンジンの寿命は `/music` 配下にいる間だけ。他の画面へ遷移したら audio 要素ごと破棄する。
 */

import { type MusicTrack, useMusicStore } from '@/store/useMusicStore'

import {
  clearPrefetchedAudio,
  prefetchAudio,
  prefetchKey,
  releasePrefetchedAudio,
  takePrefetchedAudio,
} from './music-audio-prefetch'
import { invalidateSigned, peekSigned, resolveSigned, streamPath, withStem as appendStem } from './music-signed-url'
import type { StemVariant } from './music-stem-plan'

/** 次曲の音声データを先読みし始める「残り秒数」 */
const PREFETCH_LEAD_SECONDS = 60

/** 自動再生ロック解除に使う無音WAV（ヘッダのみ・長さ0秒） */
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='

let audioElement: HTMLAudioElement | null = null
let initialized = false

/**
 * このエンジンが再生を担当しているか。
 * ミックス再生に交代している間は false になり、ストアの変化も audio イベントも無視する。
 */
let elementActive = true

/** 選択中の分離済み音源。null なら原曲 */
let elementStem: StemVariant | null = null

/**
 * Media Session を掴んでいるか。
 * ハンドラを登録している間は OS 側の再生コマンド（ロック画面・イヤホン・車載機器・
 * 割り込み終了後の自動再開など）で音楽が鳴り始めるため、実際に音を出したときだけ true にする。
 */
let sessionActive = false

/** audio.src はブラウザが絶対URL化するため、比較用に自前で保持する */
let currentSourceUrl: string | null = null

/**
 * src 差し替え時にブラウザが発火させる pause イベントを無視するための残数。
 * これを無視しないと、曲送りのたびにストアが「一時停止」に落ちてしまう。
 */
let pauseSuppressions = 0

/**
 * 自分から play() を呼んだか。
 * ブラウザ／OS は音声フォーカスの復帰やメディア通知からの再開で、
 * 一時停止したままの要素を勝手に再生し直すことがあるため、それを識別して弾く。
 */
let playIntent = false

/**
 * src 差し替え・play() の世代番号。
 * 曲送りを連打すると、前の曲の play() が新しい load に中断されて AbortError で reject する。
 * 世代が進んでいれば「古い再生要求が捨てられただけ」と判断し、停止扱いにしない。
 */
let playbackGeneration = 0

/**
 * これから読み込む音源の開始位置（読み込み待ちの間だけ非 null）。
 * 署名付きURLの取得を待つ間も前の曲は鳴り続けて timeupdate を飛ばすため、
 * この値が入っている間はストアの currentTime を更新しない。
 * 更新してしまうと、次の曲が前の曲の位置から始まる。
 */
let pendingStartAt: number | null = null

/** ストア購読の解除関数（HMR で古いエンジンを破棄するために保持する） */
let unsubscribeStore: (() => void) | null = null

const withStem = (url: string): string => appendStem(url, elementStem)

const currentTrack = (): MusicTrack | undefined => {
  const { queue, currentIndex } = useMusicStore.getState()
  return queue.at(currentIndex)
}

const getAudio = (): HTMLAudioElement => {
  audioElement ??= new Audio()
  audioElement.preload = 'auto'
  return audioElement
}

/** 自動再生ロック解除用の無音を鳴らしている最中か（この間のイベントは無視する） */
const isUnlockSource = (): boolean => currentSourceUrl === SILENT_WAV

/**
 * 現在の再生位置を秒で返す（曲を読み込んでいなければ null）
 *
 * ストアの currentTime は timeupdate（約4Hz）でしか更新されないため、
 * 歌詞のカラオケ表示のように滑らかさが要るものはここから直接読む。
 */
export const getPlaybackTime = (): number | null => {
  if (!audioElement || !currentSourceUrl || isUnlockSource()) return null
  return audioElement.currentTime
}

// ============================================================
// キュー操作（ストアのロジックと同じ順序で次曲を決める）
// ============================================================

const getNextIndex = (): number | null => {
  const { queue, currentIndex, repeatMode } = useMusicStore.getState()
  if (queue.length === 0) return null
  if (currentIndex + 1 < queue.length) return currentIndex + 1
  if (repeatMode === 'all') return 0
  return null
}

/** 次に流れる曲（1曲リピート中やキューの末尾なら null） */
const getNextTrack = (): MusicTrack | null => {
  const { repeatMode, queue } = useMusicStore.getState()
  if (repeatMode === 'one') return null

  const nextIndex = getNextIndex()
  if (nextIndex === null) return null
  return queue.at(nextIndex) ?? null
}

/** 次に流れる曲の署名付きURLを先読みしておく */
const prefetchNextUrl = (): void => {
  const nextTrack = getNextTrack()
  if (!nextTrack) return
  void resolveSigned(streamPath(nextTrack.episodeId))
}

const stripFragment = (src: string): string => src.replace(/#.*$/, '')

/** 次に流れる曲の音声データを先読みしておく（繋ぎ目の無音を防ぐ） */
const prefetchNextAudio = (): void => {
  const nextTrack = getNextTrack()
  if (!nextTrack) return

  const stem = elementStem
  void resolveSigned(streamPath(nextTrack.episodeId)).then((url) => {
    if (!url) return
    const inUse = currentSourceUrl ? stripFragment(currentSourceUrl) : null
    prefetchAudio(prefetchKey(nextTrack.episodeId, stem), appendStem(url, stem), inUse)
  })
}

/** 曲の音源URLを同期的に返す。先読み済みの音声データがあればそれを優先する */
const peekTrackSource = (episodeId: number): string | null => {
  const blobUrl = takePrefetchedAudio(prefetchKey(episodeId, elementStem))
  if (blobUrl) return blobUrl
  const url = peekSigned(streamPath(episodeId))
  return url ? withStem(url) : null
}

/** 先読みした音声データから離れたら解放する */
const releaseBlobSource = (previous: string | null, next: string | null): void => {
  if (!previous?.startsWith('blob:')) return
  const base = stripFragment(previous)
  if (next && stripFragment(next) === base) return
  releasePrefetchedAudio(base)
}

// ============================================================
// Media Session
// ============================================================

const hasMediaSession = (): boolean => 'mediaSession' in navigator

const buildMetadata = (track: MusicTrack, artworkUrl: string | null): MediaMetadata =>
  new MediaMetadata({
    title: track.title,
    artist: track.artist ?? undefined,
    album: track.album ?? undefined,
    artwork: artworkUrl ? [{ src: artworkUrl, sizes: '512x512', type: 'image/webp' }] : [],
  })

const applyMetadata = (track: MusicTrack | undefined): void => {
  if (!hasMediaSession()) return
  if (!track) {
    navigator.mediaSession.metadata = null
    return
  }

  const { thumbnail } = track
  if (!thumbnail) {
    navigator.mediaSession.metadata = buildMetadata(track, null)
    return
  }

  const cached = peekSigned(thumbnail)
  navigator.mediaSession.metadata = buildMetadata(track, cached)
  if (cached) return

  void resolveSigned(thumbnail).then((url) => {
    if (!url) return
    // 取得を待っている間に曲が変わっていたら上書きしない
    if (currentTrack()?.episodeId !== track.episodeId) return
    navigator.mediaSession.metadata = buildMetadata(track, url)
  })
}

const setPlaybackState = (state: MediaSessionPlaybackState): void => {
  if (!hasMediaSession() || !sessionActive) return
  navigator.mediaSession.playbackState = state
}

const updatePositionState = (): void => {
  if (!hasMediaSession() || !audioElement) return
  const { duration, currentTime, playbackRate } = audioElement
  if (!Number.isFinite(duration) || duration <= 0) return

  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate,
      position: Math.min(Math.max(currentTime, 0), duration),
    })
  } catch {
    // setPositionState 未対応のブラウザでは無視する
  }
}

const ACTION_HANDLERS: [MediaSessionAction, MediaSessionActionHandler][] = [
  ['play', () => useMusicStore.getState().setPlaying(true)],
  ['pause', () => useMusicStore.getState().setPlaying(false)],
  ['previoustrack', () => useMusicStore.getState().playPrev()],
  ['nexttrack', () => useMusicStore.getState().playNext()],
  [
    'seekto',
    (details) => {
      if (details.seekTime === undefined) return
      useMusicStore.getState().seekTo(details.seekTime)
    },
  ],
]

const setActionHandlers = (enabled: boolean): void => {
  if (!hasMediaSession()) return
  for (const [action, handler] of ACTION_HANDLERS) {
    try {
      navigator.mediaSession.setActionHandler(action, enabled ? handler : null)
    } catch {
      // 未対応のアクションは無視する
    }
  }
}

/** 実際に音が鳴り始めたタイミングで OS のメディアコントロールを掴む */
const activateSession = (): void => {
  if (sessionActive) return
  sessionActive = true
  setActionHandlers(true)
  applyMetadata(currentTrack())
}

/** メディアコントロールを手放す。以降 OS の再生コマンドでは鳴らない */
const deactivateSession = (): void => {
  if (!sessionActive) return
  sessionActive = false
  setActionHandlers(false)
  if (!hasMediaSession()) return
  navigator.mediaSession.metadata = null
  navigator.mediaSession.playbackState = 'none'
}

/** 再生を止めてメディアセッションを手放す */
const stopPlayback = (): void => {
  playIntent = false
  audioElement?.pause()
  useMusicStore.getState().setPlaying(false)
  deactivateSession()
}

/**
 * 動画など別のメディアが再生を始めたらメディアセッションを譲る。
 * 譲らないと、動画側の再生・停止に反応した OS の play コマンドで音楽が勝手に鳴り出す。
 */
const handleForeignPlay = (event: Event): void => {
  if (!sessionActive) return
  const target = event.target
  if (target === audioElement || !(target instanceof HTMLMediaElement)) return
  // サムネイルのプレビュー再生などはセッションを奪わない
  if (target.muted || target.volume === 0) return

  stopPlayback()
}

// ============================================================
// 再生制御
// ============================================================

const safePlay = async (): Promise<void> => {
  const audio = getAudio()
  const generation = ++playbackGeneration
  playIntent = true
  try {
    await audio.play()
    if (generation !== playbackGeneration) return
    setPlaybackState('playing')
  } catch (err) {
    // 後から曲送り・シークが入っていれば、この play() は役目を終えている
    if (generation !== playbackGeneration) return
    // 新しい load / pause による中断。次の再生要求が控えているので停止扱いにしない
    if (err instanceof DOMException && err.name === 'AbortError') return

    console.warn('[MusicAudio] 再生に失敗しました:', err)
    playIntent = false
    useMusicStore.getState().setPlaying(false)
  }
}

/** src を差し替える。既に同じ音源なら何もしない */
const setSource = (url: string, startAt = 0): boolean => {
  const audio = getAudio()
  // 読み込み前に currentTime は代入できないので、開始位置は Media Fragment で渡す
  const src = startAt > 0 ? `${url}#t=${startAt}` : url
  if (currentSourceUrl === src) return false

  // src の差し替えは再生中の要素を一時停止扱いにして pause イベントを飛ばす
  if (!audio.paused) pauseSuppressions += 1

  // 差し替え前に発行した play() の結果は捨てる
  playbackGeneration += 1
  const previous = currentSourceUrl
  currentSourceUrl = src
  audio.src = src
  releaseBlobSource(previous, src)
  return true
}

/**
 * 現在のトラックを audio 要素へ反映する（必要なら再生も開始する）
 *
 * startAt 省略時は、音楽画面へ戻ってきたときなどに離れる前の位置から続きを聞けるよう
 * ストアの currentTime を引き継ぐ。曲を切り替えたときは 0 を明示して頭から鳴らす。
 */
const syncSource = async (startAt?: number): Promise<void> => {
  const audio = getAudio()
  const track = currentTrack()

  if (!track) {
    pendingStartAt = null
    stopPlayback()
    releaseBlobSource(currentSourceUrl, null)
    currentSourceUrl = null
    audio.removeAttribute('src')
    audio.load()
    return
  }

  // 前の曲の timeupdate に汚染される前に、await より先に開始位置を確定させる
  const desiredStart = startAt ?? useMusicStore.getState().currentTime
  pendingStartAt = desiredStart

  const cached = peekTrackSource(track.episodeId)
  const signed = cached ? null : await resolveSigned(streamPath(track.episodeId))
  const src = cached ?? (signed ? withStem(signed) : null)
  if (!src) {
    pendingStartAt = null
    useMusicStore.getState().setPlaying(false)
    return
  }

  // 取得を待っている間に曲が変わっていたら破棄する
  if (currentTrack()?.episodeId !== track.episodeId) return

  // src が変わらなければ loadedmetadata は来ないので、ここで待ちを解除する
  if (!setSource(src, desiredStart)) pendingStartAt = null
  if (useMusicStore.getState().isPlaying) await safePlay()
}

/**
 * 自動再生ロックの解除。
 * iOS はユーザー操作起点で一度 play() が成功した要素を「許可済み」として記憶するため、
 * 最初のタップで無音を鳴らしておけば、以降は曲送り時に非同期で play() できる。
 */
const unlockAutoplay = (): void => {
  const audio = getAudio()
  if (currentSourceUrl) return

  currentSourceUrl = SILENT_WAV
  audio.src = SILENT_WAV
  audio
    .play()
    .then(() => audio.pause())
    .catch(() => {
      // 解除できなくても通常の再生操作でリカバリできる
      currentSourceUrl = null
    })
}

// ============================================================
// audio イベント
// ============================================================

/** 無音でのロック解除中と、ミックス再生に交代している間はイベントを無視する */
const ignoreEvents = (): boolean => !elementActive || isUnlockSource()

const handleTimeUpdate = (): void => {
  if (ignoreEvents()) return
  // 次の曲の読み込み待ち。ここで前の曲の位置を書き込むと、その位置から次の曲が始まる
  if (pendingStartAt !== null) return
  const audio = getAudio()
  useMusicStore.getState().setCurrentTime(audio.currentTime)

  const remaining = audio.duration - audio.currentTime
  if (Number.isFinite(remaining) && remaining <= PREFETCH_LEAD_SECONDS) prefetchNextAudio()
}

const handleLoadedMetadata = (): void => {
  if (ignoreEvents()) return
  const audio = getAudio()
  useMusicStore.getState().setDuration(audio.duration)

  // Media Fragment が効かなかった場合の保険
  const startAt = pendingStartAt
  pendingStartAt = null
  if (startAt !== null && startAt > 0 && audio.currentTime === 0) audio.currentTime = startAt
  useMusicStore.getState().setCurrentTime(audio.currentTime)
  updatePositionState()
}

const handleEnded = (): void => {
  if (ignoreEvents()) return
  const store = useMusicStore.getState()

  if (store.repeatMode === 'one') {
    const audio = getAudio()
    audio.currentTime = 0
    void safePlay()
    store.setCurrentTime(0)
    return
  }

  const nextIndex = getNextIndex()
  if (nextIndex === null) {
    stopPlayback()
    store.setCurrentTime(0)
    return
  }

  // 先読みできていれば await を挟まずに次曲へ繋ぐ。
  // ここで無音が続くと iOS がバックグラウンド再生の権限を剥奪する。
  const nextTrack = store.queue.at(nextIndex)
  const nextSrc = nextTrack ? peekTrackSource(nextTrack.episodeId) : null
  if (nextSrc && setSource(nextSrc)) void safePlay()

  // ストア更新後の購読側は src が一致するため二重ロードにはならない
  store.playAt(nextIndex)
}

const handlePlay = (): void => {
  if (isUnlockSource()) return

  // ミックス再生へ交代している間に鳴り出すと音が二重になるので、何もかも無視して止める
  if (!elementActive) {
    audioElement?.pause()
    return
  }

  // 自分から play() していないのに再生が始まったら OS 由来の自動再開とみなして即座に止める。
  // 端末スリープからの復帰や音声フォーカスの返却で、一時停止したままの曲が鳴り出すのを防ぐ。
  // 'play' は実際に音が出る 'playing' より先に発火するため、ここで止めれば音は漏れない。
  // ストアが再生中なら曲送り途中の play() 由来なので、playIntent の取りこぼしがあっても止めない。
  if (!playIntent && !useMusicStore.getState().isPlaying) {
    stopPlayback()
    return
  }

  pauseSuppressions = 0
  activateSession()
  useMusicStore.getState().setPlaying(true)
  setPlaybackState('playing')
  updatePositionState()
}

const handlePause = (): void => {
  if (ignoreEvents()) return
  if (pauseSuppressions > 0) {
    pauseSuppressions -= 1
    return
  }
  playIntent = false
  useMusicStore.getState().setPlaying(false)
  setPlaybackState('paused')
}

const handleError = (): void => {
  // 音源未設定（停止直後）やロック解除用の無音でのエラーは無視する
  if (!currentSourceUrl || ignoreEvents()) return

  console.error('[MusicAudio] 音源の読み込みに失敗しました:', getAudio().error)
  // 期限切れの署名付きURLを掴んでいる可能性があるためキャッシュを捨てる
  const track = currentTrack()
  if (track) invalidateSigned(streamPath(track.episodeId))
  pendingStartAt = null
  releaseBlobSource(currentSourceUrl, null)
  currentSourceUrl = null
  stopPlayback()
}

// ============================================================
// ストアの購読
// ============================================================

const subscribeStore = (): void => {
  unsubscribeStore = useMusicStore.subscribe((state, prev) => {
    if (!elementActive) return

    const track = state.queue.at(state.currentIndex)
    const prevTrack = prev.queue.at(prev.currentIndex)
    const trackChanged = track?.episodeId !== prevTrack?.episodeId

    if (trackChanged) {
      if (sessionActive) applyMetadata(track)
      // 別の曲へ移ったときは必ず頭から鳴らす
      void syncSource(0)
      prefetchNextUrl()
      return
    }

    if (state.seekVersion !== prev.seekVersion) {
      pendingStartAt = null
      getAudio().currentTime = state.currentTime
      updatePositionState()
    }

    if (state.isPlaying === prev.isPlaying) return

    if (!state.isPlaying) {
      getAudio().pause()
      setPlaybackState('paused')
      return
    }
    // 一時停止から復帰する際、音源が未設定なら読み込みから行う
    if (currentSourceUrl && !isUnlockSource()) void safePlay()
    else void syncSource()
  })
}

// ============================================================
// ミックス再生との交代
// ============================================================

/** 現在の再生位置（秒）。音源を持っていなければストアの位置を返す */
export const getElementPosition = (): number => {
  if (!audioElement || !currentSourceUrl || isUnlockSource()) return useMusicStore.getState().currentTime
  return audioElement.currentTime
}

/**
 * このエンジンに再生を任せる。
 * stem を変えた場合も含めて指定位置から音源を張り直す（ストアが再生中ならそのまま鳴り始める）。
 */
export const activateElementEngine = async (stem: StemVariant | null, position: number): Promise<void> => {
  elementStem = stem
  elementActive = true
  await syncSource(position)
}

/**
 * 再生を手放して停止する。
 * 切替中に UI が点滅しないよう、ストアの isPlaying は意図的に触らない。
 */
export const deactivateElementEngine = (): number => {
  const position = getElementPosition()
  if (!elementActive) return position
  elementActive = false
  playIntent = false
  pendingStartAt = null
  audioElement?.pause()
  deactivateSession()
  return position
}

/**
 * 再生エンジンを初期化する（`/music` 配下に入ったときに呼ぶ）
 */
export const initMusicAudioEngine = (): void => {
  if (initialized) return
  initialized = true

  const audio = getAudio()
  audio.addEventListener('timeupdate', handleTimeUpdate)
  audio.addEventListener('loadedmetadata', handleLoadedMetadata)
  audio.addEventListener('ended', handleEnded)
  audio.addEventListener('play', handlePlay)
  audio.addEventListener('pause', handlePause)
  audio.addEventListener('error', handleError)

  subscribeStore()

  document.addEventListener('pointerdown', unlockAutoplay, { once: true })
  document.addEventListener('play', handleForeignPlay, true)
}

/**
 * エンジンを破棄する（`/music` 配下から離れたときと HMR 時に呼ぶ）
 *
 * audio 要素ごと消すのが重要。一時停止のまま残すと、OS からは「再開可能なメディア」に見え、
 * 端末スリープ復帰や音声フォーカスの返却で勝手に鳴り出す。
 */
export const disposeMusicAudioEngine = (): void => {
  // 先に購読を切る。残したままだと stopPlayback のストア更新で購読側の処理が走ってしまう
  unsubscribeStore?.()
  unsubscribeStore = null
  stopPlayback()
  document.removeEventListener('pointerdown', unlockAutoplay)
  document.removeEventListener('play', handleForeignPlay, true)

  if (audioElement) {
    audioElement.removeEventListener('timeupdate', handleTimeUpdate)
    audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
    audioElement.removeEventListener('ended', handleEnded)
    audioElement.removeEventListener('play', handlePlay)
    audioElement.removeEventListener('pause', handlePause)
    audioElement.removeEventListener('error', handleError)
    audioElement.removeAttribute('src')
    audioElement.load()
    audioElement = null
  }
  clearPrefetchedAudio()

  playIntent = false
  pendingStartAt = null
  currentSourceUrl = null
  elementActive = true
  elementStem = null
  initialized = false
}

if (import.meta.hot) {
  import.meta.hot.dispose(disposeMusicAudioEngine)
}
